/**
 * When an admin changes a store's secondary phone (the general transfer
 * number), other transfer numbers that were the same as the old one may be
 * stale copies. The edit-store confirm step asks whether to move them too.
 * Only numbers that matched the old secondary number are offered; numbers
 * that differ were set on purpose and are left alone.
 */

export const SECONDARY_FOLLOWER_FIELDS = [
  { key: 'order_requests_phone_number', label: 'Order Requests Phone Number' },
  { key: 'event_forwarding_number', label: 'Event Forwarding Number' },
  { key: 'catering_forwarding_number', label: 'Catering Forwarding Number' },
] as const;

export type SecondaryFollowerKey =
  (typeof SECONDARY_FOLLOWER_FIELDS)[number]['key'];

type StoreNumbers = Record<string, unknown> | null | undefined;

/** Digits only, without a leading US/Canada country code, so formatting differences still match. */
const phoneKey = (value: unknown): string => {
  const digits = typeof value === 'string' ? value.replace(/\D/g, '') : '';
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
};

const EXTENSION_FIELD: Record<SecondaryFollowerKey, string> = {
  order_requests_phone_number: 'order_requests_phone_number_ext',
  event_forwarding_number: 'event_forwarding_number_ext',
  catering_forwarding_number: 'catering_forwarding_number_ext',
};

/** Digits and # only, so a stored extension matches what the form will save. */
const extensionKey = (value: unknown): string =>
  typeof value === 'string' ? value.replace(/[^0-9#]/g, '') : '';

/**
 * Transfers that still match the saved secondary number and extension, when
 * the form changes that number or its extension. A transfer with its own
 * extension was set on purpose and is left alone. Clearing the secondary
 * number offers nothing.
 */
export function numbersSharingOldSecondary(
  saved: StoreNumbers,
  current: StoreNumbers,
): SecondaryFollowerKey[] {
  const before = phoneKey(saved?.location_secondary_phone_number);
  const after = phoneKey(current?.location_secondary_phone_number);
  const beforeExt = extensionKey(saved?.location_secondary_phone_number_ext);
  const afterExt = extensionKey(current?.location_secondary_phone_number_ext);
  if (!before || !after || (before === after && beforeExt === afterExt)) {
    return [];
  }
  return SECONDARY_FOLLOWER_FIELDS.map((f) => f.key).filter((key) => {
    const ext = EXTENSION_FIELD[key];
    return (
      phoneKey(saved?.[key]) === before &&
      phoneKey(current?.[key]) === before &&
      extensionKey(saved?.[ext]) === beforeExt &&
      extensionKey(current?.[ext]) === beforeExt
    );
  });
}

/** Copy the form's new secondary number and extension into the chosen fields. */
export function applyNewSecondary<T extends Record<string, unknown>>(
  current: T,
  keys: SecondaryFollowerKey[],
): T {
  if (keys.length === 0) return current;
  const next: Record<string, unknown> = { ...current };
  keys.forEach((key) => {
    next[key] = current.location_secondary_phone_number;
    next[EXTENSION_FIELD[key]] =
      current.location_secondary_phone_number_ext ?? '';
  });
  return next as T;
}

export const secondaryFollowerLabel = (key: SecondaryFollowerKey) =>
  SECONDARY_FOLLOWER_FIELDS.find((f) => f.key === key)?.label ?? key;
